import React, { useMemo } from "react";

type Props = {
  label: string;
  value: string;
  onChange: (value: string) => void;
  maxLength?: number;
  subLabel?: string;
  error?: string;
  isEditable?: boolean;
  allowCopy?: boolean;
  isRequired?: boolean;
  id?: string;
};

const TextInput: React.FC<Props> = ({
  label,
  value,
  onChange,
  maxLength,
  subLabel,
  error,
  isEditable = true,
  allowCopy = false,
  isRequired = false,
  id,
}) => {
  const inputId = useMemo(() => {
    const source = id || label || 'field';
    const safe = String(source)
      .toLowerCase()
      .replace(/\s+/g, '-')
      .replace(/[^a-z0-9_-]/g, '');
    return id || `onb-input-${safe}-${Math.random().toString(36).slice(2, 8)}`;
  }, [id, label]);
  const helpId = `${inputId}-help`;
  const errorId = `${inputId}-error`;

  const describedBy = [subLabel ? helpId : null, error ? errorId : null]
    .filter(Boolean)
    .join(" ");

  const handleCopy = async () => {
    if (!value) return;
    try {
      await navigator.clipboard.writeText(value);
      // UX: self-voicing for screen readers
      const sr = document.createElement("div");
      sr.setAttribute("role", "status");
      sr.setAttribute("aria-live", "polite");
      sr.className = "sr-only";
      sr.textContent = "Copied to clipboard";
      document.body.appendChild(sr);
      setTimeout(() => document.body.removeChild(sr), 800);
    } catch (err) {
      // no-op
      // eslint-disable-next-line no-console
      console.error("Failed to copy:", err);
    }
  };

  return (
    <div className="form-group">
      <label htmlFor={inputId} className={`col-form-label ${isRequired ? 'required' : ''}`}>
        {label}
      </label>

      {subLabel && (
        <small id={helpId} className="form-text text-muted">
          {subLabel}
        </small>
      )}

      {!isEditable ? (
        <div 
          className="input-group"
          style={{
            display: "flex",
            gap: "1rem"
          }}
        >
          <input
            required={isRequired? true: false}
            id={inputId}
            type="text"
            className={`form-control flex-fill ${error ? "is-invalid" : ""}`}
            value={value || ""}
            readOnly
            aria-invalid={!!error}
            aria-describedby={describedBy || undefined}
          />
          {allowCopy && (
            <div 
              className="input-group-append"
              style={{
                
              }}
            >
              <button
                type="button"
                className="btn btn-outline-secondary btn-sm"
                onClick={handleCopy}
                aria-label="Copy value"
                disabled={!value}
              >
                Copy
              </button>
            </div>
          )}
          {error && (
            <div id={errorId} className="invalid-feedback">
              {error}
            </div>
          )}
        </div>
      ) : (
        <>
            <input
              required={isRequired? true: false}
              id={inputId}
              type="text"
              className={`form-control flex-fill ${error ? "is-invalid" : ""}`}
              value={value}
              maxLength={maxLength || undefined}
              onChange={(e) => onChange(e.target.value)}
              aria-invalid={!!error}
              aria-describedby={describedBy || undefined}
          />
          {error && (
            <div id={errorId} className="invalid-feedback">
              {error}
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default TextInput;
